import React, { useState, useEffect } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import toast from "react-hot-toast";
import { FaHistory, FaRobot } from "react-icons/fa";

import SABTestScore from "./SABTestScores";
import { matchHLATyping } from "./MatchScores";
import { analyzeCREG } from "./CREGAnalysis";
import { EpletClientService } from "../../../utils/epletService";

const GroqMatchDonors = ({
  recipient,
  donor,
  recipientClinicals,
  donorClinicals,
  setShowResponse,
  showResponse,
  aiResponse,
  tabId,
  setAiAnalysisData,
  activeTab,
  setShowHistoryModal,
}) => {
  // GROQ API configuration as primary AI
  const GROQ_API_KEY =
    process.env.REACT_APP_GROQ_API_KEY || process.env.GROQ_API_KEY;
  const GROQ_API_URL = "https://api.groq.com/openai/v1/chat/completions";
  const DEFAULT_MODEL = "openai/gpt-oss-120b";

  // Debug environment variables
  console.log("Environment check:");
  console.log(
    "REACT_APP_GROQ_API_KEY:",
    process.env.REACT_APP_GROQ_API_KEY ? "✅ Found" : "❌ Not found"
  );
  console.log(
    "GROQ_API_KEY:",
    process.env.GROQ_API_KEY ? "✅ Found" : "❌ Not found"
  );
  console.log(
    "Final GROQ_API_KEY:",
    GROQ_API_KEY ? "✅ Available" : "❌ Missing"
  );
  console.log("Using Model:", DEFAULT_MODEL);

  const [localAiResponse, setLocalAiResponse] = useState("");
  const [currentHLAScore, setCurrentHLAScore] = useState(null);
  const [currentSABScore, setCurrentSABScore] = useState(null);
  const [error, setError] = useState(null);
  const [cregAnalysisResults, setCregAnalysisResults] = useState(null);
  const [epletAnalysis, setEpletAnalysis] = useState(null);
  const [diagnosis, setDiagnosis] = useState("");
  const [stage, setStage] = useState("idle");
  const [researchData, setResearchData] = useState(null);
  const [currentAiModel, setCurrentAiModel] = useState(null);
  const [showRawReport, setShowRawReport] = useState(false);

  useEffect(() => {
    console.log("aiResponse prop changed:", aiResponse);
    setLocalAiResponse(aiResponse || "");
  }, [aiResponse]);

  useEffect(() => {
    const calculateScores = async () => {
      try {
        if (recipientClinicals && donorClinicals) {
          console.log("Calculating primary HLA score...");
          const hlaScoreResult = matchHLATyping(
            recipientClinicals,
            donorClinicals
          );
          setCurrentHLAScore(hlaScoreResult);

          console.log("Calculating primary SAB score...");
          const sABScoreResult = await SABTestScore(recipient._id, donor._id);
          setCurrentSABScore(sABScoreResult);
        } else {
          console.log("Clinical data not yet available");
          setCurrentHLAScore(null);
          setCurrentSABScore(null);
        }
      } catch (err) {
        console.error("Error calculating scores:", err);
        setError(`Failed to calculate scores: ${err.message}`);
        setCurrentHLAScore(null);
        setCurrentSABScore(null);
      }
    };

    if (recipient && donor && recipientClinicals && donorClinicals) {
      calculateScores();
    }
  }, [recipient, donor, recipientClinicals, donorClinicals]);

  useEffect(() => {
    const performAnalysis = () => {
      if (recipientClinicals && donorClinicals && recipient && donor) {
        const cregResults = analyzeCREG(recipientClinicals, donorClinicals);
        setCregAnalysisResults(cregResults);

        // Compute molecular eplet mismatch analysis
        try {
          if (recipientClinicals.hlaTyping && donorClinicals.hlaTyping) {
            const epletResult = EpletClientService.calculateMismatchClientSide(
              recipientClinicals.hlaTyping,
              donorClinicals.hlaTyping,
              recipientClinicals.hlaAntibody || []
            );
            setEpletAnalysis(epletResult);
          }
        } catch (epletErr) {
          console.warn("Eplet client calculation error:", epletErr);
        }
      }
    };

    performAnalysis();
  }, [recipient, donor, recipientClinicals, donorClinicals]);

  useEffect(() => {
    if (stage === "complete") {
      setStage("idle");
      if (setAiAnalysisData) {
        setAiAnalysisData((prev) => ({
          ...prev,
          [tabId]: { ...prev[tabId], aiResponse: "" },
        }));
      }
    }
  }, [activeTab, setAiAnalysisData, tabId]);

  // GROQ API function as primary AI
  const generateWithGroq = async (prompt, maxTokens = 4000) => {
    try {
      console.log("🧠 Using GPT-OSS-120B model for generation...");

      // Use the API key from .env
      const apiKey = GROQ_API_KEY;

      console.log("GROQ API Key available:", !!apiKey);
      console.log("GROQ API Key length:", apiKey?.length || 0);

      if (!apiKey) {
        throw new Error("GROQ API key not found in environment variables");
      }

      const response = await fetch(GROQ_API_URL, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${apiKey}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          model: DEFAULT_MODEL,
          messages: [
            {
              role: "system",
              content:
                "You are a board-certified Clinical Transplant Specialist with extensive experience in immunology and transplant medicine. You have access to comprehensive medical knowledge and provide detailed, evidence-based medical analysis with comprehensive citations and research references. Focus on clinical accuracy, evidence-based recommendations, and professional medical reporting standards.",
            },
            {
              role: "user",
              content: prompt,
            },
          ],
          max_tokens: maxTokens,
          temperature: 0.1,
          top_p: 0.9,
        }),
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error(`GROQ API error details:`, errorText);
        throw new Error(`GROQ API error: ${response.status} - ${errorText}`);
      }

      const data = await response.json();
      const content = data.choices[0]?.message?.content;

      if (!content) {
        throw new Error("No content received from GROQ API");
      }

      console.log("✅ GROQ generation successful");
      return content;
    } catch (error) {
      console.error("❌ GROQ generation failed:", error);
      throw error;
    }
  };

  // Simplified AI generation using GROQ only
  const generateWithAI = async (prompt, maxTokens = 4000) => {
    try {
      const content = await generateWithGroq(prompt, maxTokens);
      return { content, model: "GROQ GPT-OSS-120B" };
    } catch (error) {
      console.error("❌ AI generation failed:", error.message);
      throw new Error(`AI generation failed: ${error.message}`);
    }
  };

  // Simple AI analysis function
  const aiRun = async () => {
    setStage("diagnosing");
    setError(null);
    setLocalAiResponse("");
    if (setAiAnalysisData) {
      setAiAnalysisData((prev) => ({
        ...prev,
        [tabId]: { ...prev[tabId], aiResponse: "" },
      }));
    }

    try {
      console.log("🧠 Starting AI analysis...");

      const prompt = `
You are a Board-Certified Clinical Transplant Specialist. Analyze the following living donor kidney transplant compatibility data and provide a comprehensive clinical consultation report using proper Markdown formatting.

**Recipient Information:**
- Recipient Code: ${recipient?.recipient_code || "N/A"}
- Blood Group: ${recipient?.blood_group?.label || "N/A"}
- HLA Typing: ${JSON.stringify(recipientClinicals?.hlaTyping || {})}

**Donor Information:**
- Donor Code: ${donor?.donor_code || "N/A"}
- Blood Group: ${donor?.blood_group?.label || "N/A"}
- HLA Typing: ${JSON.stringify(donorClinicals?.hlaTyping || {})}

**Immunological Compatibility Profiling:**
- Serologic HLA Score: ${JSON.stringify(currentHLAScore || {})}
- Single Antigen Bead (SAB) MFI DSA Score: ${JSON.stringify(currentSABScore || {})}
- CREG Cross-Reactivity: ${JSON.stringify(cregAnalysisResults || {})}

**Molecular HLA Eplet Analysis (EpRegistry Standard - www.epregistry.com.br):**
- Total Eplet Mismatches: ${epletAnalysis?.totalMismatches ?? "N/A"} (${epletAnalysis?.confirmedMismatches ?? 0} Antibody-Verified)
- Class I Eplet Load (A, B, C): ${epletAnalysis?.classI ?? "N/A"} (Threshold: <10 Low Risk, >16 High Risk)
- HLA-DRB1 Eplet Load: ${epletAnalysis?.drb1 ?? "N/A"} (Threshold: <7 Low Risk, >10 High Risk)
- HLA-DQB1 Eplet Load: ${epletAnalysis?.dqb1 ?? "N/A"} (Threshold: <9 Low Risk, >14 High Risk)
- Overall Eplet Risk Tier: ${epletAnalysis?.riskTierLabel ?? "N/A"} (Compatibility Score: ${epletAnalysis?.epletScore ?? "N/A"}%)
- 5-Year dnDSA Predicted Risk: ${epletAnalysis?.dnDsaPrediction ?? "N/A"}

Please provide a structured clinical consultation report with the following sections:
1. **Executive Summary**: Clear go/no-go recommendation for paired kidney exchange.
2. **Immunological Compatibility**: Detailed breakdown of ABO, antigen HLA, SAB MFI, and CREG cross-reactivity.
3. **Molecular Eplet Mismatch & dnDSA Forecasting**: Explicitly analyze the HLA-DRB1 and HLA-DQB1 eplet loads based on international cohorts (Wiebe et al., Senev et al.) and assess the risk of late microvascular inflammation or antibody-mediated rejection (ABMR).
4. **Physician Recommendations & Personalized Protocol**: Specific recommendations on:
   - Induction immunosuppression (e.g. Basiliximab vs. rATG / Thymoglobulin).
   - Maintenance CNI target (Tacrolimus trough level targets).
   - Steroid strategy (Rapid withdrawal vs. maintenance).
   - Post-transplant Luminex surveillance schedule.

Use proper markdown formatting with headers, tables, callouts, and bullet points.
      `;

      const result = await generateWithAI(prompt);
      setCurrentAiModel(result.model);

      const reportWithCodes = `Recipient Code: ${
        recipient?.recipient_code || "N/A"
      }
Donor Code: ${donor?.donor_code || "N/A"}

${result.content}`;

      setLocalAiResponse(reportWithCodes);
      if (setAiAnalysisData) {
        setAiAnalysisData((prev) => ({
          ...prev,
          [tabId]: { showResponse: true, aiResponse: reportWithCodes },
        }));
      }

      setStage("complete");
      console.log("✅ AI analysis completed successfully");
    } catch (error) {
      console.error("❌ AI analysis failed:", error);
      setError(`Failed to generate report: ${error.message}`);
      setStage("idle");
      toast.error(`Error: ${error.message}`);
    }
  };

  return (
    <div className="container p-1 mx-auto">
      <div className="flex items-center justify-end mb-4 space-x-4">
        <button
          onClick={() => setShowHistoryModal(true)}
          className="flex items-center px-4 py-2 font-bold text-white bg-green-500 rounded hover:bg-green-700"
        >
          <FaHistory className="mr-2" /> View Report History
        </button>
        <button
          onClick={async () => {
            try {
              console.log("Testing GROQ API...");
              const result = await generateWithGroq(
                "Test. Respond with 'API Working'",
                50
              );
              toast.success(`GROQ API Test: ${result}`);
            } catch (error) {
              toast.error(`GROQ API Test Failed: ${error.message}`);
            }
          }}
          className="flex items-center px-3 py-2 text-sm font-medium text-white bg-purple-500 rounded hover:bg-purple-700"
        >
          🔧 Test API
        </button>
        <button
          onClick={aiRun}
          className="flex items-center px-4 py-2 font-bold text-white bg-blue-500 rounded hover:bg-blue-700 disabled:bg-blue-300"
          disabled={stage === "diagnosing" || stage === "generating"}
        >
          <FaRobot className="mr-2" />
          {stage === "idle" || stage === "complete"
            ? "Generate GPT-OSS-120B Analysis"
            : "Generating..."}
        </button>
      </div>

      {(stage === "diagnosing" || stage === "generating") && (
        <div className="flex flex-col items-center justify-center p-6 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg border">
          <div className="w-16 h-16 border-t-4 border-b-4 border-blue-500 rounded-full animate-spin"></div>
          <p className="mt-4 text-xl font-semibold text-gray-800">
            🧠 Generating Clinical Analysis with GPT-OSS-120B
          </p>
          <div className="mt-2 text-sm text-blue-600">
            Analyzing transplant compatibility data...
          </div>
        </div>
      )}

      {localAiResponse && (
        <div className="p-8 mb-8 bg-gradient-to-r from-gray-50 to-blue-50 rounded-lg border shadow-lg">
          <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded">
            <div className="text-sm font-medium text-green-800 mb-1">
              📋 Evidence-Based Clinical Report Generated
            </div>
            <div className="text-xs text-green-700">
              This report integrates AI-powered research findings with clinical
              analysis. All citations include direct links for verification.
              {currentAiModel && (
                <div className="mt-1 text-blue-700">
                  🤖 Generated using: {currentAiModel} (Primary AI Model)
                </div>
              )}
              <div className="mt-1 text-gray-600">
                📊 Report Length: {localAiResponse.length.toLocaleString()}{" "}
                characters
              </div>
            </div>
            <div className="mb-4 flex items-center justify-between">
              <button
                onClick={() => setShowRawReport(!showRawReport)}
                className="px-3 py-1 text-xs bg-gray-200 hover:bg-gray-300 rounded transition-colors"
              >
                {showRawReport ? "📋 Show Formatted" : "🔧 Show Raw"}
              </button>
            </div>
          </div>

          <div className="prose prose-lg max-w-none">
            {showRawReport ? (
              // Raw text view
              <div className="bg-gray-50 border rounded p-4">
                <h3 className="text-lg font-semibold mb-3 text-gray-800">
                  Raw Report Content
                </h3>
                <pre className="bg-white p-4 rounded border text-sm overflow-auto max-h-96 whitespace-pre-wrap text-gray-800">
                  {localAiResponse}
                </pre>
              </div>
            ) : (
              // Formatted markdown view
              <ReactMarkdown
                remarkPlugins={[remarkGfm]}
                components={{
                  a: ({ node, ...props }) => (
                    <a
                      {...props}
                      className="text-blue-600 hover:text-blue-800 underline font-medium"
                      target="_blank"
                      rel="noopener noreferrer"
                    />
                  ),
                  h1: ({ node, ...props }) => (
                    <h1
                      {...props}
                      className="text-3xl font-bold text-gray-900 border-b-2 border-blue-500 pb-2 mb-4"
                    />
                  ),
                  h2: ({ node, ...props }) => (
                    <h2
                      {...props}
                      className="text-2xl font-semibold text-gray-800 mt-6 mb-3 border-l-4 border-blue-500 pl-3"
                    />
                  ),
                  h3: ({ node, ...props }) => (
                    <h3
                      {...props}
                      className="text-xl font-medium text-gray-700 mt-4 mb-2"
                    />
                  ),
                  table: ({ node, ...props }) => (
                    <div className="overflow-x-auto my-4">
                      <table
                        {...props}
                        className="min-w-full border-collapse border border-gray-300 bg-white"
                      />
                    </div>
                  ),
                  th: ({ node, ...props }) => (
                    <th
                      {...props}
                      className="border border-gray-300 bg-gray-100 px-4 py-3 text-left font-semibold text-gray-900"
                    />
                  ),
                  td: ({ node, ...props }) => (
                    <td
                      {...props}
                      className="border border-gray-300 px-4 py-3 text-gray-800"
                    />
                  ),
                  strong: ({ node, ...props }) => (
                    <strong
                      {...props}
                      className="font-semibold text-gray-900"
                    />
                  ),
                  p: ({ node, ...props }) => (
                    <p
                      {...props}
                      className="mb-4 text-gray-800 leading-relaxed"
                    />
                  ),
                }}
              >
                {localAiResponse}
              </ReactMarkdown>
            )}
          </div>
        </div>
      )}

      {error && (
        <div className="p-4 mb-4 text-red-700 bg-red-100 rounded-lg">
          {error}
        </div>
      )}
    </div>
  );
};

export default GroqMatchDonors;
